import {EventEmitter, Injectable} from '@angular/core';
import { AuthActions, AuthService, IAuthAction } from 'ionic-appauth-custom';
import { Subscription } from 'rxjs';
import { ConnectivityService } from '../connectivity.service';
import { NavigationService } from '../navigation.service';
import { UserService } from '../user.service';
import {ModalController} from '@ionic/angular';
import {AccountCreationSuccessfulComponent} from './account-creation-successful/account-creation-successful.component';

@Injectable({
  providedIn: 'root'
})
export class CheckSigninService {

  public success = false;
  private userServiceReadySubscription: Subscription;
  public runStartupAnimation = false;
  private startupAnimationFinishedEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  public startupAnimationFinished = false;
  public startupAnimationRunning = true;
  private signinCheckRunning = false;
  private signinRunning = false;
  public loginFailed = false;
  private firstLoginHappened = false;
  public showLoginScreen = false;
  public cameFromLoginSuccess = false;
  private turnLocalUserIntoRealAccountRunning = false;

  constructor(private auth: AuthService,
              private userService: UserService,
              private navigationService: NavigationService,
              private connectivityService: ConnectivityService,
              private modalController: ModalController) {
    this.auth.events$.subscribe((action) => this.onAction(action));
    this.listenToAppOnline();
   }

  public async checkSignIn(force: boolean = false, fromAppResume: boolean = false): Promise<void> {
    if (!this.connectivityService.appIsOnline) {
      console.log('App is offline...');
      this.afterLogin().then(); // login offline
      return;
    }
    const hasStorageUser = await this.userService.hasStorageUser();
    if (!hasStorageUser) {
      console.log('No user yet!');
      this.showLoginScreen = true;
      // this.auth.tokenWithClientCredentials().then();
      return;
    }
    const isLocalUser = await this.userService.isLocalUser();
    if (isLocalUser) {
      console.log('Local user...');
      if (!fromAppResume) {
        this.afterLogin().then(); // login offline
      }
      return;
    }
    if (!this.signinCheckRunning || force) {
      this.signinCheckRunning = true;
      console.log(new Date(), 'Checking sign-in');
      if (force) {
        this.auth.loadTokenFromStorage().then();
      }
    }
  }

  public startLoginProcess(force: boolean = false, fromAppResume: boolean = false): void {
    if (this.cameFromLoginSuccess === true) {
      return;
    }
    this.loginFailed = false;
    this.checkSignIn(force, fromAppResume).then();
    setTimeout(() => {
      if (!this.success && !this.signinRunning) {
        this.loginFailed = true;
      }
    }, 5000);
  }

  public startAnimation(): void {
    if (this.runStartupAnimation && !this.startupAnimationFinished) {
      this.startupAnimationFinished = false;
      setTimeout(() => {
        this.runStartupAnimation = false;
        this.startupAnimationFinished = true;
        this.startupAnimationFinishedEmitter.emit(true);
        this.startupAnimationRunning = false;
      }, 3000);
    }
  }

  public createLocalUser(): void {
    this.userService.createLocalUser();
    this.auth.tokenWithClientCredentials().then();
  }

  public turnLocalUserIntoRealAccount(identityProvider: IdentityProvider): void {
    this.turnLocalUserIntoRealAccountRunning = true;
    switch (identityProvider) {
      case IdentityProvider.idoplanty:
        this.forwardToAccountCreation(this.userService.user.id);
        return;
      case IdentityProvider.google:
        this.forwardToSignInWithGoogle(this.userService.user.id);
        return;
      case IdentityProvider.apple:
        this.forwardToSignInWithApple(this.userService.user.id);
        return;
    }
  }

  public forwardToAccountCreation(userId?: string): void {
    this.signInWithParameter('CreateAccount', userId);
  }

  public forwardToLogin(): void {
    this.signInWithParameter('Login');
  }

  public forwardToSignInWithGoogle(userId?: string): void {
    this.signInWithParameter('Google', userId);
  }

  public forwardToSignInWithApple(userId?: string): void {
    this.signInWithParameter('Apple', userId);
  }

  private async onAction(action: IAuthAction) {
    // return;
    console.log(action);
    if (action.action === AuthActions.LoadTokenFromStorageFailed ||
        action.action === AuthActions.SignInFailed ||
        action.action === AuthActions.RefreshFailed) {
      if (action.action === AuthActions.SignInFailed) { this.signinRunning = false; }
      this.success = false;
      this.signinCheckRunning = true;
      this.turnLocalUserIntoRealAccountRunning = false;
      this.runMethodAfterStartupAnimationFinished(this.signIn.bind(this));
    }
    if ((action.action === AuthActions.SignInSuccess ||
        action.action === AuthActions.LoadTokenFromStorageSuccess ||
        action.action === AuthActions.RefreshSuccess ||
        action.action === AuthActions.TokenWithClientCredentialsSuccess)
        && (!this.success || this.turnLocalUserIntoRealAccountRunning)) {
      if (action.action === AuthActions.SignInSuccess) {
        this.startupAnimationRunning = false;
        this.startupAnimationFinished = true;
        this.signinRunning = false;
        this.userService.serviceReady = false;
        this.userService.setUserInfo(this.turnLocalUserIntoRealAccountRunning);
      }
      this.afterLogin().then();
    }
    if (action.action === AuthActions.SignOutSuccess) {
      this.success = false;
      this.signinCheckRunning = true;
      this.cameFromLoginSuccess = false;
      this.signIn().then();
    }
  }

  private async signIn(): Promise<void> {
    const hasStorageUser = await this.userService.hasStorageUser();
    console.log('Has storage user: ' + hasStorageUser);
    if (!hasStorageUser) { return; }
    this.signinRunning = true;
    this.auth.signIn().then();
  }

  private signInWithParameter(parameter: string, userId?: string): void {
    this.signinRunning = true;
    const queryParameters = userId ?
        {['goTo']: parameter, ['userId']: userId}
        : {['goTo']: parameter};
    this.auth.signIn(queryParameters).then();
  }

  private async afterLogin() {
    this.success = true;
    this.signinCheckRunning = false;
    this.firstLoginHappened = true;
    if (this.userService.serviceReady){
      const authRes = await this.connectivityService.triggerAuthChecks();
      console.log('Authorization state:', authRes);
      this.afterUserServiceReady();
    } else {
      this.userServiceReadySubscription = this.userService.serviceReadySubject.subscribe(async (val: boolean) => {
        if (val) {
          const authRes = await this.connectivityService.triggerAuthChecks();
          console.log('Authorization state:', authRes);
          this.afterUserServiceReady();
          this.userServiceReadySubscription.unsubscribe();
        }
      });
    }
  }

  private afterUserServiceReady(){
    this.runMethodAfterStartupAnimationFinished(this.goToDefaultPage.bind(this));
    // bind(this) makes sure that following functions are called in the right context
  }

  private goToDefaultPage() {
    if (this.turnLocalUserIntoRealAccountRunning) {
      this.turnLocalUserIntoRealAccountRunning = false;
      this.modalController.create({
        component: AccountCreationSuccessfulComponent
      }).then(modal => modal.present().then());
    }
    this.navigationService.goToDefaultPage();
  }

  private listenToAppOnline(): void {
    this.connectivityService.appIsOnline$.subscribe(online => {
      if (online && this.firstLoginHappened) {
        this.success = false;
        this.startLoginProcess(true);
      }
    });
  }

  private runMethodAfterStartupAnimationFinished(method: () => void): void {
    if (this.startupAnimationFinished) {
      method();
      return;
    }
    this.startupAnimationFinishedEmitter.subscribe(() => {
      method();
    });
  }

}

export enum IdentityProvider {
  idoplanty, google, apple
}
